import React, { useState, useRef, useEffect, memo, useMemo, useCallback } from 'react';
import { ArrowRight, Brain, Zap, Users, Star, Shield, User } from 'lucide-react';
import AnimatedBackgroundExpert from './AnimatedBackgroundExpert';
import AnimatedBackgroundLocal from './AnimatedBackgroundLocal';
import GradientBackground from './GradientBackground';
import AnimatedBrain from './AnimatedBrain';
import CategoryBadge from './CategoryBadge';
import UserAvatar from './UserAvatar';
import QuickAnalysisForm from './QuickAnalysisForm';
import FullScreenLoader from './FullScreenLoader';
import { BackgroundAnalysisStatus } from '../types';
import { ShareService } from '../services/shareService';
import { getCategoryColors } from '../utils/categoryColors';
import { getVerdictI18nKey } from '../utils/verdictCalculator';
import Disclaimer from './Disclaimer';
import { isPrerender } from '../utils/isPrerender';
import { isChromeAndroid } from '../utils/isMobile';
import { pathWithLang } from '../utils/navigation';
import { useTranslation } from 'react-i18next';


interface AnalysisData {
  id: number;
  category: string;
  categoryColor: string;
  title: string;
  description: string;
  date: string;
  verdict: string;
  verdictColor: string;
  timestamp: number;
  isLoaded: boolean;
  share_id?: string;
  share_url?: string;
  external_id?: string;
}

interface LandingPageProps {
  onStartAnalysis: (description?: string, expert?: string, person?: string, question?: string, noQuestion?: boolean) => void;
  onCancel?: () => void;
  onAbortAnalysis?: () => void;
  onGoToForm?: () => void;
  isLoading?: boolean;
  backgroundAnalysisStatus?: BackgroundAnalysisStatus | null;
  error?: string | null;
  onClearError?: () => void;
  onClearValidationError?: () => void;
  onGoToPublicShares?: () => void; // Callback для перехода на страницу публичных анализов
  theme?: 'dark' | 'light';
}

const IS_BRAIN_ANIMATION_ENABLED = true;

// CSS анимации - отключаются для Prerender через класс .no-animations
const LANDING_PAGE_ANIMATIONS = `
  @keyframes slideDown{0%{transform:translateY(-100%);opacity:0}100%{transform:translateY(0);opacity:1}}
  
  @keyframes gentlePulse{0%,100%{opacity:0.6;transform:scale(1)}50%{opacity:1;transform:scale(1.1)}}
  
  @keyframes gentlePing{0%{transform:scale(1);opacity:0.3}75%,100%{transform:scale(1.5);opacity:0}}
  
  @keyframes vortexSlow{0%{transform:rotate(0deg) translateX(200px) rotate(0deg)}100%{transform:rotate(360deg) translateX(200px) rotate(-360deg)}}
  
  @keyframes vortexMedium{0%{transform:rotate(0deg) translateX(180px) rotate(0deg)}100%{transform:rotate(360deg) translateX(180px) rotate(-360deg)}}
  
  @keyframes vortexFast{0%{transform:rotate(0deg) translateX(160px) rotate(0deg)}100%{transform:rotate(360deg) translateX(160px) rotate(-360deg)}}
  
  @keyframes vortexOuter{0%{transform:rotate(0deg) translateX(240px) rotate(0deg)}100%{transform:rotate(360deg) translateX(240px) rotate(-360deg)}}
  
  @keyframes floatSlow{0%,100%{transform:translateY(0px) translateX(0px)}25%{transform:translateY(-20px) translateX(10px)}50%{transform:translateY(-10px) translateX(-5px)}75%{transform:translateY(-15px) translateX(8px)}}
  
  @keyframes floatMedium{0%,100%{transform:translateY(0px) translateX(0px)}33%{transform:translateY(-15px) translateX(-8px)}66%{transform:translateY(-25px) translateX(12px)}}
  
  @keyframes floatFast{0%,100%{transform:translateY(0px) translateX(0px)}50%{transform:translateY(-30px) translateX(15px)}}
  
  .animate-vortex-slow{animation:vortexSlow 20s linear infinite}
  
  .animate-vortex-medium{animation:vortexMedium 15s linear infinite}
  
  .animate-vortex-fast{animation:vortexFast 12s linear infinite}
  
  .animate-vortex-outer{animation:vortexOuter 25s linear infinite}
  
  .animate-float-slow{animation:floatSlow 8s ease-in-out infinite}
  
  .animate-float-medium{animation:floatMedium 6s ease-in-out infinite}
  
  .animate-float-fast{animation:floatFast 4s ease-in-out infinite}
  
  @keyframes fadeIn{0%{opacity:0;transform:translateY(20px)}100%{opacity:1;transform:translateY(0)}}
  
  .animate-fadeIn{animation:fadeIn 0.6s ease-out forwards}
  
  @keyframes vortexMobile{0%{transform:rotate(0deg) translateX(120px) rotate(0deg)}100%{transform:rotate(360deg) translateX(120px) rotate(-360deg)}}
  
  @keyframes vortexMobileMedium{0%{transform:rotate(0deg) translateX(100px) rotate(0deg)}100%{transform:rotate(360deg) translateX(100px) rotate(-360deg)}}
  
  @keyframes vortexMobileFast{0%{transform:rotate(0deg) translateX(80px) rotate(0deg)}100%{transform:rotate(360deg) translateX(80px) rotate(-360deg)}}
  
  .animate-vortex-mobile{animation:vortexMobile 20s linear infinite}
  
  .animate-vortex-mobile-medium{animation:vortexMobileMedium 15s linear infinite}
  
  .animate-vortex-mobile-fast{animation:vortexMobileFast 12s linear infinite}
  
  /* Отключение всех анимаций для Prerender */
  .no-animations *,
  .no-animations *::before,
  .no-animations *::after {
    animation: none !important;
    transition: none !important;
  }
  
  /* Исключение: мобильные анимации тегов продолжают работать при загрузке, но НЕ при prerender */
  .no-animations:not(.prerender) .animate-vortex-mobile {
    animation: vortexMobile 20s linear infinite !important;
  }
  .no-animations:not(.prerender) .animate-vortex-mobile-medium {
    animation: vortexMobileMedium 15s linear infinite !important;
  }
  .no-animations:not(.prerender) .animate-vortex-mobile-fast {
    animation: vortexMobileFast 12s linear infinite !important;
  }
  
  /* Упрощенные теги для проблемных мобильных устройств */
  /* ПРИМЕЧАНИЕ: После тестирования выяснилось, что эти стили не нужны */
  /* Проблема чёрных артефактов была в AnimatedBackground компонентах */
  .static-tags [class*="animate-vortex"] {
    animation: none !important;
    position: static !important;
    display: inline-block;
    margin: 4px;
  }
  
  .static-tags .absolute {
    position: static !important;
  }
`;

const CAROUSEL_ITEM_HEIGHT = 138;

const LandingPage: React.FC<LandingPageProps> = memo(({ onStartAnalysis, onCancel, isLoading = false, backgroundAnalysisStatus, error, onClearError, onGoToPublicShares, theme = 'light' }) => {
  const { t, i18n } = useTranslation('landing');
  const { t: tAnalysis } = useTranslation('analysis');
  const lang = i18n.language === 'en' ? 'en' : 'ru';
  
  // Определяем, является ли устройство проблемным (Chrome на Android)
  // для применения оптимизаций рендеринга
  const [isChromeAndroidDevice, setIsChromeAndroidDevice] = useState(false);
  
  // Состояние для водопада готовых анализов
  const [visibleAnalyses, setVisibleAnalyses] = useState(6);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allAnalysesData, setAllAnalysesData] = useState<AnalysisData[]>([]);
  const [lastLoadTime] = useState(Date.now());
  const [hasMoreData, setHasMoreData] = useState(true); // Флаг наличия дополнительных данных на сервере
  
  // Максимальное количество анализов для отображения на главной странице
  const MAX_ANALYSES = 500;
  const [isLoadingPublicShares, setIsLoadingPublicShares] = useState(false);
  const [publicSharesError, setPublicSharesError] = useState<string | null>(null);
  
  // Состояние для модального окна с видео
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  
  // Определяем тип устройства при монтировании
  // На Chrome Android отключаем AnimatedBackground для предотвращения чёрных артефактов
  useEffect(() => {
    setIsChromeAndroidDevice(isChromeAndroid());
  }, []);

  // Функция для преобразования публичных шерингов в формат AnalysisData
  const transformPublicShareToAnalysisData = (share: any): AnalysisData => {
    const shareCreatedAt = new Date(share.share_created_at);
    
    // Используем новую цветовую схему для всех 12 категорий
    const categoryColors = getCategoryColors(share.category);
    const categoryColorString = `${categoryColors.bg} ${categoryColors.text}`;

    const verdictColors = {
      'ДА': 'bg-green-100 text-green-700',
      'НЕТ': 'bg-red-100 text-red-700'
    };

    return {
      id: share.id,
      category: share.category || null,
      categoryColor: categoryColorString,
      title: share.title || 'Анализ решения',
      description: share.description || '',
      date: shareCreatedAt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      verdict: share.verdict || 'НЕТ',
      verdictColor: verdictColors[share.verdict as keyof typeof verdictColors] || 'bg-gray-100 text-gray-700',
      timestamp: shareCreatedAt.getTime(),
      isLoaded: true,
      share_id: share.share_id,
      share_url: share.share_url
    };
  };

  // Загрузка публичных шерингов по выбранному языку (карусель + список)
  const lastLoadedLangRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastLoadedLangRef.current === lang) {
      return;
    }
    lastLoadedLangRef.current = lang;

    const loadPublicShares = async () => {
      setIsLoadingPublicShares(true);
      setPublicSharesError(null);

      try {
        const initialLimit = Math.min(120, MAX_ANALYSES);
        const response = await ShareService.getPublicShares(initialLimit, 0, lang);

        if (response.data && response.data.length > 0) {
          const transformedData = response.data.map(transformPublicShareToAnalysisData);
          const limitedData = transformedData.slice(0, MAX_ANALYSES);
          setAllAnalysesData(limitedData);
          setHasMoreData(response.pagination.has_more && limitedData.length < MAX_ANALYSES);
        } else {
          setAllAnalysesData([]);
          setHasMoreData(false);
        }
      } catch (error) {
        console.error('Error loading public shares:', error);
        setPublicSharesError(error instanceof Error ? error.message : 'Ошибка загрузки данных');
        setAllAnalysesData([]);
      } finally {
        setIsLoadingPublicShares(false);
      }
    };

    loadPublicShares();
  }, [lang]);

  // Функция для загрузки дополнительных анализов (водопад)
  const loadMoreAnalyses = async () => {
    // Проверяем, не достигнут ли лимит или нет ли больше данных
    if (allAnalysesData.length >= MAX_ANALYSES || !hasMoreData) {
      return;
    }
    
    setIsLoadingMore(true);
    
    try {
      // Загружаем следующую порцию публичных шерингов
      const currentOffset = allAnalysesData.length;
      // Вычисляем, сколько еще можно загрузить до лимита
      const remainingSlots = MAX_ANALYSES - allAnalysesData.length;
      const loadAmount = Math.min(50, remainingSlots);
      
      if (loadAmount <= 0) {
        setIsLoadingMore(false);
        return;
      }
      
      const response = await ShareService.getPublicShares(loadAmount, currentOffset, lang);

      if (response.data && response.data.length > 0) {
        const transformedData = response.data.map(transformPublicShareToAnalysisData);
        // Ограничиваем до MAX_ANALYSES на случай, если API вернул больше
        const newData = [...allAnalysesData, ...transformedData].slice(0, MAX_ANALYSES);
        setAllAnalysesData(newData);
        setVisibleAnalyses(prev => Math.min(prev + transformedData.length, newData.length));
        
        // Обновляем флаг наличия данных: если достигли лимита или API сказал, что больше нет
        const reachedLimit = newData.length >= MAX_ANALYSES;
        setHasMoreData(!reachedLimit && response.pagination.has_more);
      } else {
        // Если больше нет публичных шерингов
        setHasMoreData(false);
        setVisibleAnalyses(prev => Math.min(prev + 6, allAnalysesData.length));
      }
    } catch (error) {
      // В случае ошибки просто показываем больше существующих данных
      // В случае ошибки просто показываем больше существующих данных
      // Не сбрасываем hasMoreData, чтобы можно было повторить попытку
      setVisibleAnalyses(prev => Math.min(prev + 6, allAnalysesData.length));
    } finally {
      setIsLoadingMore(false);
    }
  };


  // Обработчик скролла для автоматической подгрузки
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
        // Загружаем новые данные, если:
        // 1. Не загружается уже
        // 2. Не достигнут лимит
        // 3. Есть еще данные на сервере
        const shouldLoad = !isLoadingMore && 
                          allAnalysesData.length < MAX_ANALYSES && 
                          hasMoreData;
        
        if (shouldLoad) {
          loadMoreAnalyses();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [allAnalysesData.length, isLoadingMore, hasMoreData]);

  // Функция для обновления статуса загруженных карточек
  const markAnalysisAsLoaded = (analysisId: number) => {
    setAllAnalysesData(prev => 
      prev.map(analysis => 
        analysis.id === analysisId 
          ? { ...analysis, isLoaded: true }
          : analysis
      )
    );
  };

  // Функция для навигации к шерингу без перезагрузки
  // Принимает либо share_url (полный URL с slug), либо shareId (fallback)
  const handleShareCardClick = useCallback((shareUrlOrId: string) => {
    let targetPath: string;
    
    // Если передан share_url (содержит /share/), извлекаем путь из него
    if (shareUrlOrId.includes('/share/')) {
      // Проверяем, является ли это полным URL (начинается с http:// или https://)
      if (shareUrlOrId.startsWith('http://') || shareUrlOrId.startsWith('https://')) {
        try {
          const url = new URL(shareUrlOrId);
          targetPath = url.pathname; // Извлекаем путь, например: /share/63a1bcf6e67e69ee-strah-oshibki
        } catch {
          // Если не удалось распарсить URL, используем как есть
          targetPath = shareUrlOrId;
        }
      } else {
        // Это относительный путь, используем как есть
        targetPath = shareUrlOrId.startsWith('/') ? shareUrlOrId : `/${shareUrlOrId}`;
      }
    } else {
      // Это shareId без slug, создаем путь
      targetPath = `/share/${shareUrlOrId}`;
    }
    // Нормализуем путь: убираем языковой префикс, если есть. Для /share/xxx не добавляем языковой префикс — язык из localStorage/браузера.
    const pathWithoutLang = targetPath.replace(/^\/(ru|en)(\/|$)/, '$2') || '/';
    const isSharePath = /^\/share\//.test(pathWithoutLang);
    const pathToUse = isSharePath ? pathWithoutLang : pathWithLang(pathWithoutLang);
    window.history.pushState({}, '', pathToUse);
    
    // Триггерим событие popstate для обновления состояния приложения
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Синхронный параллакс-эффект для аннотаций
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const section = document.querySelector('.interface-showcase-section');
      
      if (section) {
        const rect = section.getBoundingClientRect();
        const sectionTop = rect.top + scrollY;
        const sectionHeight = rect.height;
        
        // Вычисляем прогресс скролла в секции (0-1)
        const scrollProgress = Math.max(0, Math.min(1, (scrollY - sectionTop + windowHeight) / (sectionHeight + windowHeight)));
        
        // Синхронное движение для всех аннотаций
        const offset = (scrollProgress - 0.5) * 30; // Одинаковая скорость для всех
        
        document.documentElement.style.setProperty('--scroll-offset-1', `${offset}px`);
        document.documentElement.style.setProperty('--scroll-offset-2', `${offset}px`);
        document.documentElement.style.setProperty('--scroll-offset-3', `${offset}px`);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // Автоматическая прокрутка в начало страницы при монтировании компонента
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Обработка закрытия модального окна по Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedVideo) {
        setSelectedVideo(null);
      }
    };

    if (selectedVideo) {
      document.addEventListener('keydown', handleEscape);
      // Блокируем прокрутку фона при открытом модальном окне
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [selectedVideo]);

  // Доступные языки (совпадают с LanguageToggle и i18n)
  const footerLanguages = useMemo(() => [
    { value: 'ru' as const, label: t('langNameRu'), flag: '🇷🇺' },
    { value: 'en' as const, label: t('langNameEn'), flag: '🇺🇸' },
  ], []);

  const handleFooterLanguageSelect = useCallback((newLang: 'ru' | 'en') => {
    if (newLang === lang) return;
    localStorage.setItem('plinus-language', newLang);
    void i18n.changeLanguage(newLang);
    const path = window.location.pathname.replace(/^\/(ru|en)(\/.*)?$/, (_, __, rest) => rest || '/');
    window.location.href = `/${newLang}${path}${window.location.search}${window.location.hash}`;
  }, [lang]);

  const features = useMemo(() => [
    { icon: Brain, title: t('feature1Title'), description: t('feature1Desc') },
    { icon: Zap, title: t('feature2Title'), description: t('feature2Desc') },
    { icon: Users, title: t('feature3Title'), description: t('feature3Desc') },
    { icon: Shield, title: t('feature4Title'), description: t('feature4Desc') },
  ], [t]);



  // Мягкие оттенки серого для анимации фона

  // Проверяем, является ли запрос от Prerender
  const isPrerenderRequest = isPrerender();

  // Отключаем анимации, если идет анализ (показываем скелетон)
  const isAnalysisRunning = backgroundAnalysisStatus && 
    backgroundAnalysisStatus.status !== 'completed' && 
    backgroundAnalysisStatus.status !== 'failed';
    
  const shouldDisableAnimations = isPrerenderRequest || isAnalysisRunning || isLoading;

  return (
    <>
      <FullScreenLoader 
        backgroundAnalysisStatus={backgroundAnalysisStatus} 
        isLoading={isLoading}
        theme={theme}
      />
      <div className={`min-h-screen bg-white relative overflow-hidden ${shouldDisableAnimations ? 'no-animations' : ''} ${isPrerenderRequest ? 'prerender' : ''}`}>
        <style dangerouslySetInnerHTML={{ __html: LANDING_PAGE_ANIMATIONS }} />
        <GradientBackground />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white">
        <div className={`absolute inset-0 ${isChromeAndroidDevice ? 'bg-gray-50' : 'bg-gradient-to-r from-gray-900/5 to-black/5'}`}></div>
        <div className="relative container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 pt-8 sm:pt-12 pb-16 z-30">
          <div className="text-center">
            <div className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-gray-100 text-gray-800 text-xs sm:text-sm font-medium mb-6 sm:mb-8">
              <Star className="w-4 h-4 mr-2" />
              {t('solutionsFound')}
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl heading-hero mb-4 sm:mb-6">
              <span className="text-primary-600">{t('heroTitlePrefix')}</span>
              <span className="bg-gradient-to-r from-primary-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">{t('heroTitleAi')}</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-4xl mx-auto text-lead">
              {t('heroLead')}
            </p>
            
            {/* Форма быстрого анализа с каруселью */}
            <div className="container mx-auto max-w-[1600px] mb-6 sm:mb-8">
              <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 items-start">
                {/* Форма */}
                <QuickAnalysisForm
                  className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 border border-white/20 relative z-20"
                  onStartAnalysis={onStartAnalysis}
                  onCancel={onCancel}
                  isLoading={isLoading}
                  backgroundAnalysisStatus={backgroundAnalysisStatus}
                  error={error}
                  onClearError={onClearError}
                />

                <PublicSharesCarousel
                  allAnalysesData={allAnalysesData}
                  isLoadingPublicShares={isLoadingPublicShares}
                  handleShareCardClick={handleShareCardClick}
                />
                    </div>
                </div>
          </div>
        </div>
      </section>

      {/* Chaos of Doubts Section - Хаос сомнений */}
      <section className="py-20 sm:py-24 bg-white relative overflow-hidden">
        {/* Анимированный фон */}
        {!isPrerenderRequest && !isChromeAndroidDevice && <AnimatedBackgroundLocal />}
        
        <div className="container mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-12 xl:px-16 relative z-10">
          {/* Заголовок */}
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl heading-section mb-4 sm:mb-6">
              {t('familiarFeeling')}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto text-lead">
              {t('whenYouTry')}
            </p>
          </div>

          {/* Основной контент */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 sm:mb-16">
            {/* Левая часть - Анимированный мозг */}
            <div className="relative h-[500px] sm:h-[600px] order-1 lg:order-1">
              {/* Вопрос над мозгом */}
              <div className="absolute top-4 left-0 right-0 z-10 text-center px-4">
                <div className="inline-block bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200/50 px-6 py-4">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800">
                    {t('sampleQuestionBrain')}
                  </h3>
                </div>
              </div>
              {IS_BRAIN_ANIMATION_ENABLED && !isPrerenderRequest ? (
              <AnimatedBrain containerClassName="w-full h-full" />
              ) : (
                <div className="w-full h-full rounded-3xl border border-gray-200/60 bg-white/80 backdrop-blur flex flex-col items-center justify-center text-center p-8 shadow-inner">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center mb-6">
                    <Brain className="w-10 h-10 text-purple-500" />
                  </div>
                  <p className="text-lg font-semibold text-gray-800 mb-2">
                    Анимация временно недоступна
                  </p>
                  <p className="text-sm text-gray-600 max-w-sm">
                    Мы улучшаем визуализацию аргументов. Секция вернётся в ближайшем обновлении.
                  </p>
                </div>
              )}
            </div>

            {/* Правая часть - Решение */}
            <div className="space-y-6 order-2 lg:order-2">
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-6 sm:p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-pink-100 rounded-xl flex items-center justify-center">
                      <span className="text-2xl">😵‍💫</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl heading-small mb-2 text-gray-900">{t('chaosTitle')}</h3>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                      {t('chaosDesc')}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl flex items-center justify-center">
                        <Brain className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl heading-small mb-2 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                        {t('withPlinusTitle')}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-sm sm:text-base mb-4">
                        {t('withPlinusDesc')}
                      </p>
                      <ul className="space-y-2 text-sm sm:text-base">
                        <li className="flex items-center text-gray-700">
                          <Zap className="w-4 h-4 mr-2 text-green-500" />
                          {t('bullet1')}
                        </li>
                        <li className="flex items-center text-gray-700">
                          <Zap className="w-4 h-4 mr-2 text-green-500" />
                          {t('bullet2')}
                        </li>
                        <li className="flex items-center text-gray-700">
                          <Zap className="w-4 h-4 mr-2 text-green-500" />
                          {t('bullet3')}
                        </li>
                        <li className="flex items-center text-gray-700">
                          <Zap className="w-4 h-4 mr-2 text-green-500" />
                          {t('bullet4')}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Декоративные элементы */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-purple-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-blue-200/30 rounded-full blur-3xl"></div>
      </section>

      {/* Interface Showcase Section - Как работает сервис */}
      <section id="features" className="interface-showcase-section py-24 bg-gray-50 relative z-5">
        {/* Анимированный фон с плюсами и минусами */}
        {!isPrerenderRequest && !isChromeAndroidDevice && <AnimatedBackgroundLocal />}
        <div className="container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 relative z-30">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl heading-section mb-4 sm:mb-6">
              {t('sectionDispelTitle')}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto text-lead">
              {t('sectionDispelLead')}
            </p>
          </div>

          {/* Большие скриншоты с табами */}
          <div className="space-y-12 sm:space-y-16 lg:space-y-20">
            
            {/* Блок 1 - Задайте вопрос */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
              <div className="order-1 lg:order-1 flex justify-center">
                  <div className="relative w-full max-w-md mx-auto">
                    {/* Большой скриншот */}
                    <div className="relative cursor-pointer group" onClick={() => !isPrerenderRequest && setSelectedVideo('/images/474ED828-6274-4B71-8030-CD756F9138EC.mov')}>
                      {!isPrerenderRequest ? (
                        <video 
                          src="/images/474ED828-6274-4B71-8030-CD756F9138EC.mov" 
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-1/2 h-auto mx-auto rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl transition-transform duration-300 group-hover:scale-105"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                            if (nextElement) {
                              nextElement.style.display = 'flex';
                            }
                          }}
                        />
                      ) : (
                        <div className="w-1/2 h-auto mx-auto rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl bg-gray-200 flex items-center justify-center aspect-video">
                          <span className="text-gray-500 text-sm">{t('videoUnavailable')}</span>
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                        <div className="bg-black/70 rounded-full p-4 backdrop-blur-sm">
                          <svg className="w-12 h-12 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                        <div className="bg-black/70 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm">
                          {t('clickToView')}
                        </div>
                      </div>
                      <div className="min-h-[300px] sm:min-h-[400px] lg:min-h-[500px] flex items-center justify-center" style={{ display: 'none' }}>
                        <div className="text-center text-gray-500 px-4">
                          <div className="text-4xl sm:text-6xl lg:text-8xl mb-4 sm:mb-6">💭</div>
                          <div className="text-lg sm:text-xl lg:text-2xl font-medium">{t('interfaceInputQuestion')}</div>
                          <div className="text-sm sm:text-base lg:text-lg mt-2">{t('screenshotCaption')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
              
              <div className="order-2 lg:order-2">
                <div className="max-w-lg px-4 sm:px-0">
                  <div className="inline-flex items-center px-3 sm:px-3 sm:px-4 py-1 sm:py-2 bg-blue-100 text-blue-800 rounded-full text-xs sm:text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
                    {t('step1')}
                  </div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl heading-sub mb-4 sm:mb-6">
                    {t('step1Title')}
                  </h3>
                  <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                    {t('step1Lead')}
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step1Item1Title')}</p>
                        <p className="text-gray-600 text-sm">{t('personalToStrategic')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step1Item2Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step1Item2Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step1Item3Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step1Item3Desc')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
              
            {/* Блок 2 - Получите анализ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
              <div className="order-1 flex justify-center">
                  <div className="relative w-full max-w-md mx-auto">
                    {/* Большой скриншот - адаптивный, высокое качество */}
                    <img 
                      src="/images/Plinus_Feature_Detailed_Custom.png?v=3" 
                      alt={t('detailedAnalysisAlt')}
                      className="w-full h-auto object-contain"
                      loading="eager"
                    />
                  </div>
              </div>

              <div className="order-2">
                <div className="max-w-lg px-4 sm:px-0">
                  <div className="inline-flex items-center px-3 sm:px-3 sm:px-4 py-1 sm:py-2 bg-purple-100 text-purple-800 rounded-full text-xs sm:text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2 animate-pulse"></span>
                    {t('step2')}
                  </div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl heading-sub mb-4 sm:mb-6">
                    {t('step2Title')}
                  </h3>
                  <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                    {t('step2Lead')}
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step2Item1Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step2Item1Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step2Item2Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step2Item2Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step2Item3Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step2Item3Desc')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Блок 3 - Примите решение */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
              <div className="order-1 lg:order-1 flex justify-center">
                  <div className="relative w-full max-w-md mx-auto">
                    {/* Большой скриншот - адаптивный, высокое качество */}
                    <img 
                      src="/images/Plinus_Feature_Plan_Analyse.png?v=3" 
                      alt={t('actionPlanAlt')}
                      className="w-full h-auto object-contain"
                      loading="lazy"
                    />
                  </div>
              </div>
              
              <div className="order-2 lg:order-2">
                <div className="max-w-lg px-4 sm:px-0">
                  <div className="inline-flex items-center px-3 sm:px-3 sm:px-4 py-1 sm:py-2 bg-pink-100 text-pink-800 rounded-full text-xs sm:text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                    <span className="w-2 h-2 bg-pink-500 rounded-full mr-2 animate-pulse"></span>
                    {t('step3')}
                  </div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl heading-sub mb-4 sm:mb-6">
                    {t('step3Title')}
                  </h3>
                  <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                    {t('step3Lead')}
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step3Item1Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step3Item1Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step3Item2Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step3Item2Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step3Item3Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step3Item3Desc')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Блок 4 - Поделитесь результатом */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center mt-16 sm:mt-24">
              <div className="order-1 flex justify-center">
                  <div className="relative w-full max-w-md mx-auto">
                    {/* Большой скриншот */}
                    <img 
                      src="/images/Plinus_ShareDropdown.png" 
                      alt={t('shareResultAlt')}
                      className="w-full h-auto object-contain"
                      loading="lazy"
                    />

                    {/* Плавающие иконки-аннотации */}
                    
                    {/* Bot - Top Right */}
                    <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-8 bg-white p-3 sm:p-4 rounded-2xl shadow-xl z-10 border border-green-100">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-50 rounded-full flex items-center justify-center text-[#0088cc]">
                          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                          </svg>
                        </div>
                        <div className="hidden sm:block">
                          <p className="text-sm font-semibold text-gray-900">{t('addBotToGroup')}</p>
                          <p className="text-xs text-gray-500">{t('inGroup')}</p>
                        </div>
                      </div>
                    </div>

                    {/* PDF - Right, below Bot */}
                    <div className="absolute top-8 -right-4 sm:top-10 sm:-right-8 bg-white p-3 sm:p-4 rounded-2xl shadow-xl z-10 border border-red-100">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-50 rounded-full flex items-center justify-center text-red-600">
                          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="hidden sm:block">
                          <p className="text-sm font-semibold text-gray-900">PDF</p>
                          <p className="text-xs text-gray-500">{t('downloadReport')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>

              <div className="order-2">
                <div className="max-w-lg px-4 sm:px-0">
                  <div className="inline-flex items-center px-3 sm:px-3 sm:px-4 py-1 sm:py-2 bg-blue-100 text-blue-800 rounded-full text-xs sm:text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
                    {t('step4')}
                  </div>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl heading-sub mb-4 sm:mb-6">
                    {t('step4Title')}
                  </h3>
                  <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                    {t('step4Lead')}
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step4Item1Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step4Item1Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step4Item2Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step4Item2Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-green-600 text-sm">✓</span>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">{t('step4Item3Title')}</p>
                        <p className="text-gray-600 text-sm">{t('step4Item3Desc')}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Ссылка на бота */}
                  <div className="mt-6 sm:mt-8">
                    <a 
                      href="https://t.me/PlinusBot"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-[#0088cc] text-white font-semibold rounded-lg hover:bg-[#0077b3] transition-all duration-300 transform hover:scale-105 shadow-lg"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                      </svg>
                      @PlinusBot
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Модальное окно для видео */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div 
            className="relative w-full max-w-5xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Кнопка закрытия */}
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
              aria-label={t('closeBtn')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Контент модального окна (Видео или Изображение) */}
            {selectedVideo?.toLowerCase().match(/\.(mp4|mov|webm)$/) && !isPrerenderRequest ? (
            <video 
              src={selectedVideo}
              autoPlay
              loop
              muted
              playsInline
              controls
              className="w-full h-auto max-h-[90vh] object-contain"
            />
            ) : (
              <img 
                src={selectedVideo || ''}
                alt="Full view"
                className="w-full h-auto max-h-[90vh] object-contain"
              />
            )}
          </div>
        </div>
      )}

      {/* Облако тегов - Решайте любые вопросы */}
      <section className="py-20 bg-white relative overflow-hidden z-5">
        {/* Анимированный фон с плюсами и минусами */}
        {!isPrerenderRequest && !isChromeAndroidDevice && <AnimatedBackgroundLocal />}
        
        {/* Декоративные элементы */}
        <div className="absolute inset-0 z-[1]">
            <div className="absolute top-20 left-10 w-16 h-16 bg-blue-200/30 rounded-full blur-xl animate-float-slow"></div>
            <div className="absolute top-40 right-20 w-20 h-20 bg-purple-200/30 rounded-full blur-xl animate-float-medium"></div>
            <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-green-200/30 rounded-full blur-xl animate-float-fast"></div>
            <div className="absolute bottom-40 right-1/3 w-14 h-14 bg-pink-200/30 rounded-full blur-xl animate-float-slow"></div>
            <div className="absolute top-1/2 left-10 w-18 h-18 bg-indigo-200/30 rounded-full blur-xl animate-float-medium"></div>
            <div className="absolute top-1/3 right-10 w-16 h-16 bg-cyan-200/30 rounded-full blur-xl animate-float-fast"></div>
          </div>
        
        <div className="container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 relative z-20">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl heading-section mb-4 sm:mb-6">
              {t('solveAnyQuestions')}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto text-lead">
              {t('tagCloudLead')}{' '}
              <span className="font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {t('expertsWord')}
              </span>{' '}
              {t('simultaneouslyWord')}
            </p>
              </div>
          
          {/* Облако тегов */}
          <div className="relative min-h-[400px] sm:min-h-[500px] md:min-h-[600px] flex flex-col md:flex-row items-center justify-center">
            {/* Мобильная версия */}
            <div className="md:hidden relative w-full h-[300px] sm:h-[350px] flex items-center justify-center mb-8">
              {/* Центральная иконка логотипа */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
                  <img 
                    src="/images/animatedrefined4-animated.svg" 
                    alt="ПЛИНУС" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              
              {/* Внешний круг - медленные теги */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile" style={{ animationDelay: '0s' }}>
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagJobChange')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile" style={{ animationDelay: '3.1s' }}>
                  <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-2 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagInvestments')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile" style={{ animationDelay: '6.2s' }}>
                  <div className="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-3 py-2 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagRelationships')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile" style={{ animationDelay: '9.3s' }}>
                  <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-3 py-2 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagHealth')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile" style={{ animationDelay: '12.4s' }}>
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 py-2 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagEducation')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile" style={{ animationDelay: '15.5s' }}>
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-2 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagTravel')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile" style={{ animationDelay: '18.6s' }}>
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-3 py-2 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagTech')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile" style={{ animationDelay: '21.7s' }}>
                  <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white px-3 py-2 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagHobby')}</span>
                  </div>
                </div>
              </div>
              
              {/* Средний круг - средние теги */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-medium" style={{ animationDelay: '1s' }}>
                  <div className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-2.5 py-1.5 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagStartup')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-medium" style={{ animationDelay: '4.1s' }}>
                  <div className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-2.5 py-1.5 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagRealEstate')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-medium" style={{ animationDelay: '7.2s' }}>
                  <div className="bg-gradient-to-r from-pink-400 to-rose-500 text-white px-2.5 py-1.5 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagFamily')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-medium" style={{ animationDelay: '10.3s' }}>
                  <div className="bg-gradient-to-r from-emerald-400 to-teal-500 text-white px-2.5 py-1.5 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagSports')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-medium" style={{ animationDelay: '13.4s' }}>
                  <div className="bg-gradient-to-r from-purple-400 to-violet-500 text-white px-2.5 py-1.5 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagLanguages')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-medium" style={{ animationDelay: '16.5s' }}>
                  <div className="bg-gradient-to-r from-orange-400 to-amber-500 text-white px-2.5 py-1.5 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagRelocation')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-medium" style={{ animationDelay: '19.6s' }}>
                  <div className="bg-gradient-to-r from-indigo-400 to-blue-500 text-white px-2.5 py-1.5 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagGadgets')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-medium" style={{ animationDelay: '22.7s' }}>
                  <div className="bg-gradient-to-r from-cyan-400 to-sky-500 text-white px-2.5 py-1.5 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagCreativity')}</span>
                  </div>
                </div>
              </div>
              
              {/* Внутренний круг - быстрые теги */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-fast" style={{ animationDelay: '2s' }}>
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-2 py-1 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagSalary')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-fast" style={{ animationDelay: '5.1s' }}>
                  <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-2 py-1 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">Крипто</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-fast" style={{ animationDelay: '8.2s' }}>
                  <div className="bg-gradient-to-r from-rose-500 to-pink-600 text-white px-2 py-1 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagKids')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-fast" style={{ animationDelay: '11.3s' }}>
                  <div className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-2 py-1 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagNutrition')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-fast" style={{ animationDelay: '14.4s' }}>
                  <div className="bg-gradient-to-r from-violet-500 to-purple-600 text-white px-2 py-1 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagCourses')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-fast" style={{ animationDelay: '17.5s' }}>
                  <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-2 py-1 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagEmigration')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-fast" style={{ animationDelay: '20.6s' }}>
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-2 py-1 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagSmartHome')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-mobile-fast" style={{ animationDelay: '23.7s' }}>
                  <div className="bg-gradient-to-r from-sky-500 to-cyan-600 text-white px-2 py-1 rounded-full shadow-lg text-center whitespace-nowrap">
                    <span className="text-xs font-medium">{t('tagPhoto')}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Центральный призыв к действию */}
            <div className="relative z-40 text-center w-full md:absolute md:top-1/2 md:left-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2">
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-6 sm:p-8 border border-white/20 max-w-md mx-auto">
                <div className="hidden md:flex w-16 h-16 items-center justify-center mx-auto mb-4">
                  <img 
                    src="/images/animatedrefined4-animated.svg" 
                    alt="ПЛИНУС" 
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <h3 className="text-2xl heading-sub mb-3">
                  {t('oneQuestionOneVerdict')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('ctaLead')}
                </p>
                <a 
                  href={`/${lang}/analysis`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-900 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold hover:bg-black transition-all duration-300 transform hover:scale-105 shadow-lg relative z-20 inline-block"
                >
                  {t('tryNow')}
                </a>
              </div>
            </div>
            
            {/* Водоворот тегов */}
            <div className="absolute inset-0 hidden md:block z-30">
              {/* Внешний круг - медленные теги */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-outer" style={{ animationDelay: '0s' }}>
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs sm:text-sm font-medium">{t('tagJobChange')}</span>
              </div>
              </div>
            </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-outer" style={{ animationDelay: '3.1s' }}>
                  <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 sm:px-5 py-2 sm:py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-sm font-medium">{t('tagInvestments')}</span>
              </div>
            </div>
          </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-outer" style={{ animationDelay: '6.2s' }}>
                  <div className="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-3 sm:px-5 py-2 sm:py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-sm font-medium">{t('tagRelationships')}</span>
                </div>
                </div>
              </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-outer" style={{ animationDelay: '9.3s' }}>
                  <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-3 sm:px-5 py-2 sm:py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-sm font-medium">{t('tagHealth')}</span>
                </div>
              </div>
            </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-outer" style={{ animationDelay: '12.4s' }}>
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 sm:px-5 py-2 sm:py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-sm font-medium">{t('tagEducation')}</span>
                </div>
                </div>
              </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-outer" style={{ animationDelay: '15.5s' }}>
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 sm:px-5 py-2 sm:py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-sm font-medium">{t('tagTravel')}</span>
                </div>
              </div>
            </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-outer" style={{ animationDelay: '18.6s' }}>
                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-3 sm:px-5 py-2 sm:py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-sm font-medium">{t('tagTech')}</span>
                </div>
                </div>
              </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-outer" style={{ animationDelay: '21.7s' }}>
                  <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white px-3 sm:px-5 py-2 sm:py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-sm font-medium">{t('tagHobby')}</span>
                </div>
              </div>
              </div>

              {/* Средний круг - средние теги */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-medium" style={{ animationDelay: '1s' }}>
                  <div className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagStartup')}</span>
                  </div>
            </div>
          </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-medium" style={{ animationDelay: '4.1s' }}>
                  <div className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagRealEstate')}</span>
          </div>
        </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-medium" style={{ animationDelay: '7.2s' }}>
                  <div className="bg-gradient-to-r from-pink-400 to-rose-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagFamily')}</span>
                  </div>
                </div>
          </div>
          
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-medium" style={{ animationDelay: '10.3s' }}>
                  <div className="bg-gradient-to-r from-emerald-400 to-teal-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagSports')}</span>
                </div>
              </div>
            </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-medium" style={{ animationDelay: '13.4s' }}>
                  <div className="bg-gradient-to-r from-purple-400 to-violet-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagLanguages')}</span>
                </div>
              </div>
            </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-medium" style={{ animationDelay: '16.5s' }}>
                  <div className="bg-gradient-to-r from-orange-400 to-amber-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagRelocation')}</span>
                </div>
              </div>
            </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-medium" style={{ animationDelay: '19.6s' }}>
                  <div className="bg-gradient-to-r from-indigo-400 to-blue-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagGadgets')}</span>
                </div>
              </div>
            </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-medium" style={{ animationDelay: '22.7s' }}>
                  <div className="bg-gradient-to-r from-cyan-400 to-sky-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagCreativity')}</span>
                </div>
              </div>
            </div>

              {/* Внутренний круг - быстрые теги */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-fast" style={{ animationDelay: '2s' }}>
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-2 sm:px-3 py-1 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagSalary')}</span>
                </div>
              </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-fast" style={{ animationDelay: '5.1s' }}>
                  <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-2 sm:px-3 py-1 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagCrypto')}</span>
                  </div>
            </div>
          </div>

              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-fast" style={{ animationDelay: '8.2s' }}>
                  <div className="bg-gradient-to-r from-rose-500 to-pink-600 text-white px-2 sm:px-3 py-1 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagKids')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-fast" style={{ animationDelay: '11.3s' }}>
                  <div className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white px-2 sm:px-3 py-1 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagNutrition')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-fast" style={{ animationDelay: '14.4s' }}>
                  <div className="bg-gradient-to-r from-violet-500 to-purple-600 text-white px-2 sm:px-3 py-1 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagCourses')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-fast" style={{ animationDelay: '17.5s' }}>
                  <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white px-2 sm:px-3 py-1 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagEmigration')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-fast" style={{ animationDelay: '20.6s' }}>
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-2 sm:px-3 py-1 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagSmartHome')}</span>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="animate-vortex-fast" style={{ animationDelay: '23.7s' }}>
                  <div className="bg-gradient-to-r from-sky-500 to-cyan-600 text-white px-2 sm:px-3 py-1 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-110">
                    <span className="text-xs sm:text-xs font-medium">{t('tagPhoto')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Отзывы пользователей - продолжение секции */}
        <div className="container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 relative z-30 mt-20">
          {/* Подробная статистика */}
          <div className="text-center mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="text-3xl heading-sub mb-1">10,000+</div>
                <div className="text-sm text-gray-600">{t('analysesConducted')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl heading-sub mb-1">95%</div>
                <div className="text-sm text-gray-600">{t('satisfiedUsers')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl heading-sub mb-1">{t('avgAnalysisTimeShort')}</div>
                <div className="text-sm text-gray-600">{t('avgAnalysisTime')}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl heading-sub mb-1">4.9/5</div>
                <div className="text-sm text-gray-600">{t('avgRating')}</div>
              </div>
            </div>
          </div>

          {/* Отзывы как логическое продолжение */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Отзыв 1 */}
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20 hover:shadow-3xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center text-white font-bold text-lg mr-4">
                  А
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{t('testimonial1Name')}</h4>
                  <p className="text-sm text-gray-500">{t('testimonial1Role')}</p>
                </div>
              </div>
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                {t('testimonial1')}
              </p>
            </div>

            {/* Отзыв 2 */}
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20 hover:shadow-3xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center text-white font-bold text-lg mr-4">
                  М
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{t('testimonial2Name')}</h4>
                  <p className="text-sm text-gray-500">{t('testimonial2Role')}</p>
                </div>
              </div>
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                {t('testimonial2')}
              </p>
            </div>

            {/* Отзыв 3 */}
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20 hover:shadow-3xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center text-white font-bold text-lg mr-4">
                  Е
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{t('testimonial3Name')}</h4>
                  <p className="text-sm text-gray-500">{t('testimonial3Role')}</p>
                </div>
              </div>
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                {t('testimonial3')}
              </p>
            </div>
          </div>

        </div>
      </section>



      {/* Эксперты и известные личности - Рекламный баннер */}
      <section id="experts" className="py-16 bg-gray-900 relative overflow-hidden z-10">
        {/* Анимированный фон с плюсами и минусами под залитым фоном */}
        {!isPrerenderRequest && !isChromeAndroidDevice && <AnimatedBackgroundExpert />}
        
        {/* Декоративные элементы */}
        <div className="absolute inset-0 bg-black/10 z-10"></div>
        <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
            <div className="absolute bottom-10 right-10 w-32 h-32 bg-yellow-400/20 rounded-full blur-2xl"></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-pink-400/20 rounded-full blur-lg"></div>
          </div>
        
        <div className="relative container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
              <Star className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-white font-semibold text-sm">{t('expertsBannerNew')}</span>
              </div>
            <h2 className="text-4xl md:text-6xl heading-hero mb-6 leading-tight">
              <span className="text-white">{t('expertsBannerTitle')}</span>
              <span className="block bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {t('expertsBannerTitle2')}
              </span>
            </h2>
            <p className="text-xl text-white max-w-3xl mx-auto leading-relaxed">
              {t('expertsBannerLead')}
              </p>
            </div>

          {/* Компактный пример диалога */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                {/* Левая часть - Вопрос */}
                <div className="space-y-4">
              <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                      <User className="w-5 h-5 text-white" />
                </div>
                    <span className="text-white font-semibold">{t('yourQuestion')}</span>
              </div>
                  <div className="bg-white/20 rounded-2xl p-4 border border-white/30">
                    <p className="text-white text-sm leading-relaxed">
                      "{t('sampleExpertQuestion')}"
              </p>
                  </div>
            </div>

                {/* Правая часть - Ответ эксперта */}
                <div className="space-y-4">
              <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                      <Star className="w-5 h-5 text-white" />
                </div>
                    <span className="text-white font-semibold">{t('sampleExpertName')}</span>
              </div>
                  <div className="bg-blue-600/20 rounded-2xl p-4 border border-blue-600/30">
                    <p className="text-white text-sm leading-relaxed">
                      "{t('sampleExpertAnswer')}"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Призыв к действию */}
          <div className="text-center">
            <div className="flex flex-col sm:flex-row items-center gap-4 max-w-4xl mx-auto">
              <div className="flex items-center text-white mb-4 sm:mb-0">
                <div className="flex -space-x-2 mr-4">
                  <div className="w-8 h-8 bg-blue-600 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="text-xs font-bold text-white">ГР</span>
                  </div>
                  <div className="w-8 h-8 bg-gray-600 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="text-xs font-bold text-white">ИМ</span>
                  </div>
                  <div className="w-8 h-8 bg-gray-600 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="text-xs font-bold text-white">ОУ</span>
                  </div>
                </div>
                <div className="text-left">
                  <p className="font-semibold text-sm">{t('expertsReadyHelp')}</p>
                  <p className="text-gray-300 text-xs">{t('expertCategories')}</p>
                </div>
              </div>
              <a 
                href={`/${lang}/analysis`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 sm:px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg inline-block"
              >
                <span className="flex items-center justify-center">
                  {t('getExpertSolution')}
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Telegram Bot - Функционал */}
      <section className="py-12 bg-gray-50 relative overflow-hidden z-5">
        {/* Анимированный фон с плюсами и минусами */}
        {!isPrerenderRequest && !isChromeAndroidDevice && <AnimatedBackgroundLocal />}
        {/* Декоративные элементы */}
        <div className="absolute inset-0">
            <div className="absolute top-10 left-10 w-12 h-12 bg-blue-200/30 rounded-full blur-xl animate-float-slow"></div>
            <div className="absolute top-20 right-20 w-16 h-16 bg-purple-200/30 rounded-full blur-xl animate-float-medium"></div>
            <div className="absolute bottom-10 left-1/4 w-10 h-10 bg-green-200/30 rounded-full blur-xl animate-float-fast"></div>
            <div className="absolute bottom-20 right-1/3 w-12 h-12 bg-pink-200/30 rounded-full blur-xl animate-float-slow"></div>
          </div>
        
        <div className="container mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-12 xl:px-16 relative z-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl heading-section mb-4">
              {t('analysisInTelegram')}
            </h2>
          </div>

          {/* Основной контент */}
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Левая часть - Описание */}
            <div className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg heading-small mb-1">{t('expertSendMessage')}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('expertSendMessageDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg heading-small mb-1">{t('expertInstantAnswers')}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('expertInstantAnswersDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-violet-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg heading-small mb-1">{t('expertAlwaysHandy')}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('expertAlwaysHandyDesc')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Кнопка перехода к боту */}
              <div className="pt-2">
                <a 
                  href="https://t.me/PlinusBot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-[#0088cc] text-white font-semibold rounded-lg hover:bg-[#0077b3] transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  @PlinusBot
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <p className="text-xs text-gray-500 mt-2">
                  {t('botFreeTagline')}
                </p>
              </div>
            </div>

            {/* Правая часть - Пример семейного чата */}
            <div className="relative">
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-3 border border-white/20">
                <div className="space-y-2">
                  {/* Заголовок чата */}
                  <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
                    <div className="w-7 h-7 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-xs">{t('familyChatTitle')}</h4>
                      <p className="text-[10px] text-gray-500">{t('participantsCount')}</p>
                    </div>
                  </div>

                  {/* Сообщения */}
                  <div className="space-y-1.5">
                    {/* Сообщение мамы */}
                    <div className="flex justify-start">
                      <div className="flex items-start space-x-1.5">
                        <div className="w-5 h-5 bg-pink-400 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-[10px] font-bold">М</span>
                        </div>
                        <div className="bg-gray-100 text-gray-900 rounded-lg rounded-bl-sm px-2 py-1.5 max-w-xs">
                          <p className="text-[10px] font-semibold mb-0.5">{t('familyMom')}</p>
                          <p className="text-[10px]">{t('familyMsg1')}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">10:15</p>
                        </div>
                      </div>
                    </div>

                    {/* Сообщение папы */}
                    <div className="flex justify-start">
                      <div className="flex items-start space-x-1.5">
                        <div className="w-5 h-5 bg-blue-400 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-[10px] font-bold">П</span>
                        </div>
                        <div className="bg-gray-100 text-gray-900 rounded-lg rounded-bl-sm px-2 py-1.5 max-w-xs">
                          <p className="text-[10px] font-semibold mb-0.5">{t('familyDad')}</p>
                          <p className="text-[10px]">{t('familyMsg2')}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">10:16</p>
                        </div>
                      </div>
                    </div>

                    {/* Сообщение с упоминанием бота */}
                    <div className="flex justify-start">
                      <div className="flex items-start space-x-1.5">
                        <div className="w-5 h-5 bg-green-400 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-[10px] font-bold">С</span>
                        </div>
                        <div className="bg-gray-100 text-gray-900 rounded-lg rounded-bl-sm px-2 py-1.5 max-w-xs">
                          <p className="text-[10px] font-semibold mb-0.5">{t('familySon')}</p>
                          <p className="text-[10px]">{t('familyMsg3')}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">10:17</p>
                        </div>
                      </div>
                    </div>

                    {/* Сообщение с запросом к боту */}
                    <div className="flex justify-end">
                      <div className="bg-blue-500 text-white rounded-lg rounded-br-sm px-2 py-1.5 max-w-xs">
                        <p className="text-[10px]">{t('familyMsg4')}</p>
                        <p className="text-[10px] opacity-75 mt-0.5">10:18</p>
                      </div>
                    </div>

                    {/* Ответ бота */}
                    <div className="flex justify-start">
                      <div className="flex items-start space-x-1.5">
                        <div className="w-5 h-5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                          </svg>
                        </div>
                        <div className="bg-gray-100 text-gray-900 rounded-lg rounded-bl-sm px-2 py-1.5 max-w-xs">
                          <p className="text-[10px] font-semibold mb-1">PlinusBot</p>
                          <p className="text-[10px] leading-relaxed whitespace-pre-wrap">
                            <strong>{t('botVerdictYes')}</strong>{'\n\n'}
                            {t('botAnswerGreece')}{'\n\n'}
                            {t('botConfidence')}{'\n\n'}
                            <a href="#" className="text-blue-600 underline">plinus.ru/share/abc123</a>
                          </p>
                          <p className="text-[10px] text-gray-500 mt-1">10:19</p>
                        </div>
                      </div>
                    </div>

                    {/* Сообщение мамы после анализа */}
                    <div className="flex justify-start">
                      <div className="flex items-start space-x-1.5">
                        <div className="w-5 h-5 bg-pink-400 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-[10px] font-bold">М</span>
                        </div>
                        <div className="bg-gray-100 text-gray-900 rounded-lg rounded-bl-sm px-2 py-1.5 max-w-xs">
                          <p className="text-[10px] font-semibold mb-0.5">{t('familyMom')}</p>
                          <p className="text-[10px]">{t('familyMsg5')}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">10:20</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Unified Advantages & Testimonials Section */}
      <section className="py-12 bg-white relative z-5">
        {/* Анимированный фон с плюсами и минусами */}
        {!isPrerenderRequest && !isChromeAndroidDevice && <AnimatedBackgroundLocal />}
        <div className="container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 relative z-30">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl heading-section mb-3">
              {t('whyChooseTitle')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('whyChooseLead')}
            </p>
          </div>
          
          {/* Интересная сетка с чередованием */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group relative h-full"
                style={{
                  animationDelay: `${index * 150}ms`,
                  animation: 'fadeInUp 0.8s ease-out forwards'
                }}
              >
                <div className="flex flex-col items-center text-center h-full p-4 rounded-2xl hover:bg-gray-50/80 transition-colors duration-300">
                  {/* Иконка */}
                  <div className="flex-shrink-0 mb-6">
                    <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center shadow-xl group-hover:rotate-12 transition-transform duration-300">
                      <feature.icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  
                  {/* Контент */}
                  <div className="flex-1">
                    <h3 className="text-xl heading-sub mb-3 group-hover:text-blue-600 transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>




      {/* FAQ Section */}
      <section id="faq" className="relative py-20 overflow-hidden z-5">
        {/* Анимированный фон с плюсами и минусами */}
        {!isPrerenderRequest && !isChromeAndroidDevice && <AnimatedBackgroundLocal />}
        <div className="absolute inset-0 bg-gray-50/50 z-5"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 z-30">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl heading-section mb-4">
              {t('faqTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-16">
              {t('faqLead')}
            </p>
          </div>
          
          {/* FAQ Items */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg overflow-hidden">
              {/* FAQ Item 1 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq1Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 1 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {openFaq === 1 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq1Answer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
              {/* FAQ Item 2 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq2Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 2 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {openFaq === 2 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq2Answer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
              {/* FAQ Item 3 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq3Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 3 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {openFaq === 3 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq3Answer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
              {/* FAQ Item 4 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 4 ? null : 4)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq4Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 4 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {openFaq === 4 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq4Answer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
              {/* FAQ Item 5 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 5 ? null : 5)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq5Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 5 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {openFaq === 5 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq5Answer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
              {/* FAQ Item 6 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 6 ? null : 6)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq6Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 6 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {openFaq === 6 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq6Answer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
              {/* FAQ Item 7 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 7 ? null : 7)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq7Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 7 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                </div>
              </button>
              {openFaq === 7 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq7Answer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
              {/* FAQ Item 8 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 8 ? null : 8)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq8Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 8 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                </div>
              </button>
              {openFaq === 8 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq8Answer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
              {/* FAQ Item 9 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 9 ? null : 9)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq9Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 9 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                      </div>
              </button>
              {openFaq === 9 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq9Answer')}
                    </p>
                  </div>
                </div>
              )}
              </div>
            
              {/* FAQ Item 10 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 10 ? null : 10)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq10Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 10 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {openFaq === 10 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq10Answer')}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
              {/* FAQ Item 11 */}
              <div className="faq-item hover:bg-white/60 transition-all duration-200 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === 11 ? null : 11)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center group hover:bg-gray-50 transition-colors duration-200"
              >
                  <div className="flex items-center">
                    <h3 className="text-base font-medium text-gray-800 group-hover:text-gray-900 transition-colors">
                  {t('faq11Question')}
                </h3>
                  </div>
                  <div className={`transform transition-transform duration-200 ${openFaq === 11 ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {openFaq === 11 && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 leading-relaxed text-sm">
                      {t('faq11Answer')}
                    </p>
                  </div>
                </div>
              )}
              </div>
          </div>
          
        </div>
      </section>

      {/* Объединенный блок: Статистика + CTA - скрыт */}
      {/* <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden z-5">
        {!isPrerenderRequest && <AnimatedBackgroundLocal />}
        <div className="container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 relative z-30">
          <div className="text-center">
            <div 
              className="animate-fadeInUp"
              style={{ animationDelay: '0.3s', animationFillMode: 'both' }}
            >
              <h3 className="text-2xl md:text-3xl heading-sub mb-4">
                Готовы улучшить свои решения?
              </h3>
              <p className="text-lg text-blue-600 mb-8 max-w-2xl mx-auto">
                Получите объективный анализ вашей ситуации за считанные секунды
          </p>
          <a
            href={`/${lang}/analysis`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 relative z-20"
          >
            Начать бесплатный анализ
            <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:trangray-x-1" />
          </a>
              <p className="text-sm text-blue-200 mt-4">
                Без регистрации • Результат за 10-30 секунд
              </p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Блок с готовыми анализами пользователей - бесконечная прокрутка */}
      <section className="py-12 sm:py-16 bg-white relative z-5">
        <div className="container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 relative z-30">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl heading-sub mb-3 sm:mb-4">
              {t('readyAnalysesFromUsers')}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('lookAtRealDecisions')}
            </p>
          </div>

          {/* Индикатор загрузки публичных шерингов */}
          {isLoadingPublicShares && (
            <div className="flex justify-center mb-8">
              <div className="flex items-center space-x-2 text-gray-600">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span>{t('loadingPublicShares')}</span>
              </div>
            </div>
          )}

          {/* Сообщение об ошибке загрузки */}
          {publicSharesError && (
            <div className="flex justify-center mb-8">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md">
                <p className="text-yellow-800 text-sm">
                  {t('publicSharesError')}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 container mx-auto max-w-[1400px]">
            {allAnalysesData.slice(0, visibleAnalyses).map((analysis, index) => {
              // Определяем цвет фона карточки в зависимости от вердикта
              const cardBackgroundColor = analysis.verdict === "ДА" 
                ? "bg-green-50 border-green-200 hover:border-green-300" 
                : "bg-red-50 border-red-200 hover:border-red-300";
              
              return (
              <a 
                key={`${analysis.id}-${analysis.timestamp}`} // Уникальный ключ с временной меткой
                href={analysis.share_url || `/share/${analysis.share_id}`}
                onClick={(e) => {
                  e.preventDefault(); // Предотвращаем стандартное поведение ссылки
                  // Передаем share_url если есть, иначе share_id
                  const urlOrId = analysis.share_url || analysis.share_id!;
                  handleShareCardClick(urlOrId);
                }}
                className={`group ${cardBackgroundColor} rounded-lg border hover:shadow-md transition-all duration-200 p-3 sm:p-4 h-28 sm:h-32 flex flex-col justify-between ${
                  analysis.isLoaded ? 'opacity-100' : 'opacity-0 animate-fadeIn'
                }`}
                style={{
                  animationDelay: `${index * 100}ms`, // Задержка анимации для эффекта водопада
                }}
                onLoad={() => markAnalysisAsLoaded(analysis.id)}
              >
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <UserAvatar size="sm" />
                    <CategoryBadge 
                      category={analysis.category} 
                      size="sm" 
                    />
                    <span className="text-xs text-gray-500">{analysis.date}</span>
                    {analysis.timestamp > lastLoadTime && (
                      <span className="inline-flex items-center px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs sm:text-xs font-medium">
                        NEW
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors line-clamp-2">
                    {analysis.title}
                  </h3>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600 line-clamp-1 flex-1 mr-3">
                    {analysis.description}
                  </p>
                  <div className="flex items-center space-x-2">
                    <div className={`flex items-center px-2 py-1 rounded text-xs font-semibold ${analysis.verdictColor}`}>
                      {(() => { const k = getVerdictI18nKey(analysis.verdict); return k ? tAnalysis(k) : analysis.verdict; })()}
                    </div>
                    <div className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-700 rounded-full group-hover:bg-blue-200 group-hover:scale-110 transition-all duration-200">
                      <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </a>
              );
            })}
          </div>

          {/* Индикатор загрузки */}
          {isLoadingMore && (
            <div className="flex justify-center mt-8">
              <div className="flex items-center space-x-2 text-gray-600">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span>{t('loadMoreAnalyses')}</span>
              </div>
            </div>
          )}

          {/* Ссылка "Посмотреть все анализы" - отображается сразу для SEO */}
          {allAnalysesData.length > 0 && (
            <div className="text-center mt-12">
              <a 
                href={`/${lang}/public-shares`}
                onClick={(e) => {
                  e.preventDefault();
                  onGoToPublicShares?.();
                }}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <span>{t('viewAllAnalyses')}</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
              <p className="text-sm text-gray-600 mt-4">
                {t('plinusShown')} {allAnalysesData.length} {t('readyAnalyses')}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Disclaimer */}
      <Disclaimer className="bg-gray-50" />

      {/* Footer */}
      <footer className="bg-gradient-to-r from-gray-50 to-gray-100 text-gray-900 py-12">
        <div className="container mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl heading-sub mb-4">{t('footerTitle')}</h3>
            <p className="text-gray-600 mb-6">
              {t('footerTagline')}
            </p>
          </div>
          
          {/* Контакты в горизонтальном ряду */}
          <div className="flex flex-wrap justify-center gap-8 mb-8">
            <div className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center group-hover:bg-blue-600/30 transition-all duration-300">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-xs sm:text-sm font-medium text-gray-900">{t('emailLabel')}</p>
                <p className="text-xs text-gray-600">support@plinus.ru</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center group-hover:bg-purple-600/30 transition-all duration-300">
                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-xs sm:text-sm font-medium text-gray-900">{t('telegramLabel')}</p>
                <p className="text-xs text-gray-600">@plinus_support</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center group-hover:bg-green-600/30 transition-all duration-300">
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-xs sm:text-sm font-medium text-gray-900">{t('chatLabel')}</p>
                <p className="text-xs text-gray-600">{t('onlineSupport')}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-orange-600/20 rounded-lg flex items-center justify-center group-hover:bg-orange-600/30 transition-all duration-300">
                <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="text-xs sm:text-sm font-medium text-gray-900">{t('documentationLabel')}</p>
                <p className="text-xs text-gray-600">{t('detailedGuides')}</p>
              </div>
            </div>
          </div>
          
          {/* Ссылки, выбор языка и копирайт */}
          <div className="border-t border-gray-700 pt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
              {/* Ссылки */}
              <div className="flex flex-wrap justify-center gap-6">
              <a href="#" className="text-gray-600 hover:text-gray-900 transition-colors text-sm">
                {t('aboutService')}
              </a>
              <a 
                href={`/${lang}/privacy-policy`}
                className="text-gray-600 hover:text-gray-900 transition-colors text-sm"
              >
                {t('common:privacyPolicy')}
              </a>
              <a 
                href={`/${lang}/terms-of-use`}
                className="text-gray-600 hover:text-gray-900 transition-colors text-sm"
              >
                {t('common:termsOfUse')}
              </a>
                <a href="#" className="text-gray-600 hover:text-gray-900 transition-colors text-sm">
                  {t('termsAndDefinitions')}
                </a>
              </div>
              
              {/* Выбор языка (синхронизирован с i18n и URL) */}
              <div className="relative language-dropdown">
                <button
                  type="button"
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors text-sm px-3 py-2 rounded-lg hover:bg-gray-200"
                >
                  <span>{footerLanguages.find(l => l.value === lang)?.flag}</span>
                  <span>{footerLanguages.find(l => l.value === lang)?.label}</span>
                  <svg 
                    className={`w-4 h-4 transition-transform duration-200 ${showLanguageDropdown ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showLanguageDropdown && (
                  <div className="absolute bottom-full right-0 mb-2 w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                    {footerLanguages.map((language) => (
                      <button
                        key={language.value}
                        type="button"
                        onClick={() => {
                          setShowLanguageDropdown(false);
                          handleFooterLanguageSelect(language.value);
                        }}
                        className={`w-full px-4 py-3 text-sm text-left hover:bg-gray-700 transition-colors duration-200 flex items-center space-x-3 first:rounded-t-lg last:rounded-b-lg ${
                          lang === language.value ? 'bg-gray-700 text-white' : 'text-gray-300'
                        }`}
                      >
                        <span className="text-lg">{language.flag}</span>
                        <span>{language.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="text-gray-600 text-sm text-center">
              {t('copyright')}
            </p>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
});

interface PublicSharesCarouselProps {
  allAnalysesData: AnalysisData[];
  isLoadingPublicShares: boolean;
  handleShareCardClick: (shareUrlOrId: string) => void;
}

// Простая карусель: отображаем 5 карточек, анимируем прокрутку, затем ротируем массив
const VISIBLE_CARDS = 5;
const MIN_CAROUSEL_ITEMS = 4; // при 2–3 результатах размножаем до минимум 4, чтобы карусель крутилась
const SCROLL_INTERVAL = 3000; // 3 секунды между прокрутками
const ANIMATION_DURATION = 500; // 500ms анимация

const PublicSharesCarousel: React.FC<PublicSharesCarouselProps> = memo(({ 
  allAnalysesData, 
  isLoadingPublicShares, 
  handleShareCardClick 
}) => {
  const { t } = useTranslation('landing');
  const { t: tAnalysis } = useTranslation('analysis');
  const [startPointer, setStartPointer] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // При 2–3 результатах размножаем до минимум 4, чтобы карусель мог крутиться
  const carouselSource = useMemo(() => {
    if (allAnalysesData.length === 0) return [];
    if (allAnalysesData.length >= MIN_CAROUSEL_ITEMS) return allAnalysesData;
    const result: AnalysisData[] = [];
    while (result.length < MIN_CAROUSEL_ITEMS) {
      for (const item of allAnalysesData) result.push(item);
    }
    return result;
  }, [allAnalysesData]);

  // Получаем 5 карточек начиная с startPointer (закольцованно по carouselSource)
  const visibleCards = useMemo(() => {
    if (carouselSource.length === 0) return [];
    const cards: AnalysisData[] = [];
    const len = carouselSource.length;
    for (let i = 0; i < Math.min(VISIBLE_CARDS, len); i++) {
      const index = (startPointer + i) % len;
      cards.push(carouselSource[index]);
    }
    return cards;
  }, [carouselSource, startPointer]);

  // Автоматическая прокрутка
  useEffect(() => {
    if (carouselSource.length === 0) return;
    const len = carouselSource.length;
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setStartPointer(prev => (prev + 1) % len);
        setIsAnimating(false);
      }, ANIMATION_DURATION);
    }, SCROLL_INTERVAL);
    return () => clearInterval(interval);
  }, [carouselSource.length]);

  return (
    <div className="space-y-3 pb-4">
      <div
        className="relative h-56 sm:h-64 md:h-72 lg:h-80 overflow-hidden px-4 sm:px-6 lg:px-12 xl:px-16"
        style={{
          maskImage: 'linear-gradient(to bottom, black 0%, black 85%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 85%, transparent 100%)'
        }}
      >
        <div
          style={{
            transform: isAnimating ? `translateY(-${CAROUSEL_ITEM_HEIGHT}px)` : 'translateY(0)',
            transition: isAnimating ? `transform ${ANIMATION_DURATION}ms ease-in-out` : 'none'
          }}
        >
          {visibleCards.map((example, index) => (
            <a
              key={`${example.id}-${startPointer}-${index}`}
              href={example.share_url || `/share/${example.share_id}`}
              onClick={(e) => {
                e.preventDefault();
                const urlOrId = example.share_url || example.share_id!;
                handleShareCardClick(urlOrId);
              }}
              className={`block rounded-lg border p-3 sm:p-4 hover:shadow-md transition-shadow duration-200 overflow-hidden ${
                example.verdict === 'ДА'
                  ? 'bg-green-50 border-green-200 hover:border-green-300'
                  : 'bg-red-50 border-red-200 hover:border-red-300'
              }`}
              style={{
                height: `${CAROUSEL_ITEM_HEIGHT - 10}px`,
                marginBottom: '10px'
              }}
            >
              <div className="h-full flex flex-col justify-between overflow-hidden">
                <div className="flex-1 overflow-hidden text-left">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <UserAvatar size="sm" />
                      <CategoryBadge
                        category={example.category}
                        size="sm"
                      />
                      <span className="text-xs text-gray-500">{example.date}</span>
                    </div>
                  </div>
                  <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-1 line-clamp-2 overflow-hidden text-left">
                    {example.title}
                  </h3>
                </div>
                <div className="flex items-center justify-end">
                  <div className="flex items-center space-x-2">
                    <div className={`flex items-center px-2 py-1 rounded text-xs font-semibold ${example.verdictColor}`}>
                      {(() => { const k = getVerdictI18nKey(example.verdict); return k ? tAnalysis(k) : example.verdict; })()}
                    </div>
                    <div className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 hover:scale-110 transition-all duration-200">
                      <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>

      <div className="text-center mt-3">
        {isLoadingPublicShares ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
            <span className="text-xs text-gray-600 font-medium">{t('loadingAnalyses')}</span>
          </div>
        ) : allAnalysesData.length > 0 ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="relative">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full" style={{ animation: 'gentlePulse 3s ease-in-out infinite' }}></div>
              <div className="absolute inset-0 w-1.5 h-1.5 bg-green-500 rounded-full" style={{ animation: 'gentlePing 3s cubic-bezier(0, 0, 0.2, 1) infinite' }}></div>
            </div>
            <span className="text-xs text-gray-600 font-medium">{t('realUserAnalyses')}</span>
          </div>
        ) : (
          <a
            href="https://plinus.ru/examples"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-600 font-medium hover:text-blue-600 hover:underline transition-colors duration-200 cursor-pointer"
          >
            {t('userExamplesTitle')}
          </a>
        )}
      </div>
    </div>
  );
});

PublicSharesCarousel.displayName = 'PublicSharesCarousel';

LandingPage.displayName = 'LandingPage';

export default LandingPage;

